import React, { createContext, useContext, useMemo, useRef } from 'react';
import { useNotify } from './NotifyContext';
import { RECORD_AVATAR_COPY as COPY } from '../constants/people_messages.fr';

/** Opens a student / family fiche from an id, anywhere under the provider (no prop drilling). */
const RecordNavigationContext = createContext(null);

const findById = (rows, key, id) => (rows || []).find((row) => String(row[key]) === String(id));

/** Stable value: latest lists and handlers are read through a ref, so consumers never re-render for them. */
export function RecordNavigationProvider({ students, families, onOpenStudent, onOpenFamily, children }) {
  const { toast } = useNotify();
  const latest = useRef();
  latest.current = { students, families, onOpenStudent, onOpenFamily, toast };

  const value = useMemo(() => {
    const open = (rowsKey, idKey, handlerKey) => (id) => {
      const current = latest.current;
      const record = findById(current[rowsKey], idKey, id);
      if (!record) {
        current.toast.error(COPY.notFound);
        return;
      }
      current[handlerKey](record);
    };
    return {
      openStudent: open('students', 'student_id', 'onOpenStudent'),
      openFamily: open('families', 'family_id', 'onOpenFamily'),
    };
  }, []);

  return <RecordNavigationContext.Provider value={value}>{children}</RecordNavigationContext.Provider>;
}

/** `{ openStudent(id), openFamily(id) }`, or null outside the provider. */
export const useOpenRecord = () => useContext(RecordNavigationContext);
